import type { DialogContentProps, DialogProps } from '@radix-ui/react-dialog';
import { ToggleGroupItem } from '@radix-ui/react-toggle-group';
import { useQuery } from '@tanstack/react-query';
import type { FC } from 'react';
import { useState } from 'react';

import Icon from '@/components/Icon';
import LoadingPlaceholder from '@/components/LoadingPlaceholder';
import NetworkFetchError from '@/components/NetworkFetchError';
import Text from '@/components/Text';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ToggleGroup } from '@/components/ui/toggle-group';
import { queries } from '@/queries';
import { dateFormatter } from '@/utils/date';

import Collections from './Collections';
import Github from './Github/Github';
import Notes from './Note';
import PersonalData from './PersonalData';
import ProfileBadge from './ProfileBadge';
import Section from './Section';

type Props = {
  candidateId?: number;
  dialogProps?: DialogProps;
  dialogContentProps?: DialogContentProps;
};

const CandidatePreviewDialog: FC<Props> = ({
  candidateId,
  dialogContentProps,
  dialogProps,
}) => {
  const { data, isLoading, error, refetch, isSuccess } = useQuery({
    ...queries.externalCandidates.detail(candidateId!),
    enabled: !!candidateId,
  });

  const viewElements =
    !data || !candidateId
      ? []
      : ([
          {
            key: 'personalData',
            label: 'Personal Data',
            render: <PersonalData data={data} />,
          },
          {
            key: 'notes',
            label: 'Notes',
            render: <Notes id={candidateId} />,
          },
          {
            key: 'github',
            label: 'Github',
            render: <Github id={candidateId} />,
          },
          {
            key: 'collections',
            label: 'Collections',
            render: <Collections data={data} />,
          },
        ] as const);

  type ViewElementsKeys = (typeof viewElements)[number]['key'];
  const [currentView, setCurrentView] =
    useState<ViewElementsKeys>('personalData');

  const currentViewRender =
    viewElements.find(({ key }) => key === currentView)?.render ?? null;

  const {
    candidateDetailedInfo,
    createdAt,
    email,
    githubUrl,
    linkedinUrl,
    name,
    phoneNumber,
    updatedAt,
  } = data ?? {};

  const contacts = [
    { value: email, label: 'Email', icon: 'material-symbols:stacked-email' },
    {
      value: phoneNumber,
      label: 'Phone Number',
      icon: 'material-symbols:phone-enabled',
    },
    {
      value: linkedinUrl,
      label: 'LinkedIn',
      icon: 'mdi:linkedin',
    },
    {
      value: githubUrl,
      label: 'GitHub',
      icon: 'mdi:github',
    },
  ];

  const systemDates = [
    {
      date: createdAt,
      label: 'Created at:',
    },
    {
      date: updatedAt,
      label: 'Last update:',
    },
  ];

  return (
    <Dialog {...dialogProps}>
      <DialogContent
        {...dialogContentProps}
        className="flex h-full max-w-5xl flex-col !gap-10 text-neutral-1000"
      >
        <DialogTitle />
        {!!error && <NetworkFetchError onRefetch={refetch} />}
        {isLoading && <LoadingPlaceholder />}
        {isSuccess && (
          <>
            <div className="flex flex-col gap-1">
              {systemDates.map(
                ({ date, label }) =>
                  !!date && (
                    <div className="flex flex-row gap-1" key={label}>
                      <Text variant="body-caption" className="text-neutral-600">
                        {label}
                      </Text>
                      <Text variant="body-caption" className="text-neutral-600">
                        {dateFormatter({
                          date,
                          format: { dateStyle: 'short' },
                        })}
                      </Text>
                    </div>
                  )
              )}
            </div>
            <div className="flex w-full flex-col items-center justify-center gap-2">
              <Text variant="h3" className="font-semibold text-primary-600">
                {name}
              </Text>
              <Text>{candidateDetailedInfo?.jobTitle}</Text>
            </div>
            <div className="flex flex-col gap-1">
              {!!data && <ProfileBadge data={data} />}
              <div className="flex flex-row gap-3 rounded-lg bg-neutral-200 px-6 py-4">
                <Section label="Contact Information">
                  <div className="flex flex-col flex-wrap gap-2">
                    {contacts
                      .filter((contact) => contact.value)
                      .map((contact) => (
                        <div
                          key={`${contact.value}-${contact.label}`}
                          className="flex flex-row items-center gap-2"
                        >
                          <Icon
                            icon={contact.icon}
                            className="text-primary-600"
                          />
                          <Text variant="body-small">{contact.value}</Text>
                        </div>
                      ))}
                  </div>
                </Section>
              </div>
            </div>
            <ToggleGroup
              variant="secondary"
              type="single"
              value={currentView}
              onValueChange={(newValue: ViewElementsKeys) =>
                newValue ? setCurrentView(newValue) : undefined
              }
            >
              {viewElements.map(({ key, label }) => (
                <ToggleGroupItem key={key} value={key}>
                  {label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            {currentViewRender}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CandidatePreviewDialog;
